/**
 * The web router's answers about the person's workspace.
 *
 * On the web address the router stands in front of every workspace server. A
 * request it cannot route — `/api/*`, `/attachments*`,
 * `/.well-known/t3/environment`, `/ws` — gets `{ "state": "<state>" }` with
 * the one status `refusalFor` in `apps/web-router/src/route-decision.ts` maps
 * that state to. The client reads both, so a workspace server's own 401 or 503
 * is never taken for one.
 */

import { accountSessionStorage, type BrowserStorage } from "../../accountStorage";

export const WORKSPACE_REFUSAL_STATUS = {
  signed_out: 401,
  no_web_access: 403,
  no_team: 409,
  not_connected: 409,
  stopped: 409,
  identity_mismatch: 409,
  starting: 503,
  outage: 503,
} as const;

export type WorkspaceRefusalState = keyof typeof WORKSPACE_REFUSAL_STATUS;

export interface WorkspaceRefusal {
  readonly state: WorkspaceRefusalState;
  /** `Retry-After` in milliseconds, when the router named one. */
  readonly retryAfterMs: number | null;
}

/** The states the window shows a page for; `signed_out` is the sign-in form, `identity_mismatch` a reload. */
export type WorkspaceUnavailableState = Exclude<
  WorkspaceRefusalState,
  "signed_out" | "identity_mismatch"
>;

const isRefusalState = (value: unknown): value is WorkspaceRefusalState =>
  typeof value === "string" && Object.hasOwn(WORKSPACE_REFUSAL_STATUS, value);

/** `Retry-After` as delta-seconds or an HTTP date, relative to `now`. */
export function parseRetryAfter(value: string | null, now: number = Date.now()): number | null {
  if (value === null) return null;
  const trimmed = value.trim();
  if (/^\d+$/u.test(trimmed)) return Number(trimmed) * 1_000;
  const date = Date.parse(trimmed);
  return Number.isNaN(date) ? null : Math.max(0, date - now);
}

export function parseWorkspaceRefusal(input: {
  readonly status: number;
  readonly body: unknown;
  readonly retryAfter: string | null;
  readonly now?: number;
}): WorkspaceRefusal | null {
  if (typeof input.body !== "object" || input.body === null || !("state" in input.body)) {
    return null;
  }
  const state = input.body.state;
  if (!isRefusalState(state) || WORKSPACE_REFUSAL_STATUS[state] !== input.status) return null;
  return { state, retryAfterMs: parseRetryAfter(input.retryAfter, input.now) };
}

const REFUSAL_STATUSES: ReadonlySet<number> = new Set(Object.values(WORKSPACE_REFUSAL_STATUS));

/** Reads a response as a router refusal without consuming it. */
export async function readWorkspaceRefusal(response: Response): Promise<WorkspaceRefusal | null> {
  if (!REFUSAL_STATUSES.has(response.status)) return null;
  if (!response.headers.get("content-type")?.toLowerCase().includes("application/json")) {
    return null;
  }
  let body: unknown;
  try {
    body = await response.clone().json();
  } catch {
    return null;
  }
  return parseWorkspaceRefusal({
    status: response.status,
    body,
    retryAfter: response.headers.get("retry-after"),
  });
}

export const WORKSPACE_PROBE_PATH = "/.well-known/t3/environment";

export type WorkspaceProbe =
  | { readonly kind: "refused"; readonly refusal: WorkspaceRefusal }
  /** A workspace server answered: the router routes this session. */
  | { readonly kind: "routed" }
  /** Neither: the network failed, or something else answered. The bootstrap decides. */
  | { readonly kind: "unknown" };

/** Asks the web address whether the session's workspace answers. Never rejects. */
export async function probeWorkspace(fetchImpl: typeof fetch = fetch): Promise<WorkspaceProbe> {
  let response: Response;
  try {
    response = await fetchImpl(WORKSPACE_PROBE_PATH, {
      cache: "no-store",
      credentials: "same-origin",
      headers: { accept: "application/json" },
    });
  } catch {
    return { kind: "unknown" };
  }
  const refusal = await readWorkspaceRefusal(response);
  if (refusal !== null) return { kind: "refused", refusal };
  return response.ok ? { kind: "routed" } : { kind: "unknown" };
}

const DEFAULT_RETRY_MS = 5_000;
const MIN_RETRY_MS = 1_000;
const MAX_RETRY_MS = 60_000;
const ADMINISTRATOR_RECHECK_MS = 30_000;

/**
 * How long a page waits before asking again: the router's `Retry-After`,
 * bounded, while the workspace starts or is out; 30 s while it waits on an
 * administrator; `null` for a state no page asks about again.
 */
export function workspaceRetryDelayMs(refusal: WorkspaceRefusal): number | null {
  switch (refusal.state) {
    case "starting":
    case "outage":
      return Math.min(
        MAX_RETRY_MS,
        Math.max(MIN_RETRY_MS, refusal.retryAfterMs ?? DEFAULT_RETRY_MS),
      );
    case "no_team":
    case "not_connected":
    case "stopped":
      return ADMINISTRATOR_RECHECK_MS;
    case "signed_out":
    case "no_web_access":
    case "identity_mismatch":
      return null;
  }
}

const RELOAD_KEY = "dovod.workspace-reload-at";
const RELOAD_INTERVAL_MS = 10_000;

let reloadHolds = 0;

/**
 * Keeps `reloadOnce` from reloading until the returned function runs. A
 * sign-out holds it: its server request can meet the router's refusal, and a
 * reload then would abort the sign-out before the session ends and the
 * account's data is erased.
 */
export function holdReloads(): () => void {
  reloadHolds += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    reloadHolds -= 1;
  };
}

/**
 * Reloads the page so the router's current answer decides what it shows,
 * unless this tab already did within `RELOAD_INTERVAL_MS` (an answer that
 * survives a fresh load would otherwise reload it forever) or a hold stands.
 * Returns whether it reloads.
 */
export function reloadOnce(
  storage: BrowserStorage = accountSessionStorage,
  reload: () => void = () => window.location.reload(),
  now: number = Date.now(),
): boolean {
  if (reloadHolds > 0) return false;
  const last = Number(storage.getItem(RELOAD_KEY));
  if (Number.isFinite(last) && last > 0 && now - last < RELOAD_INTERVAL_MS) return false;
  storage.setItem(RELOAD_KEY, String(now));
  reload();
  return true;
}

/** States that pass by themselves, without an administrator's act. */
export function retriesByItself(state: WorkspaceRefusalState): boolean {
  return state === "starting" || state === "outage";
}

export interface WorkspaceWatch {
  /** A new answer for the page to show. */
  readonly onRefusal: (refusal: WorkspaceRefusal) => void;
  /** Routed again, or signed out: a fresh document decides what to show. */
  readonly onSettled: () => void;
  readonly probe: () => Promise<WorkspaceProbe>;
}

/**
 * Asks once, as the page's «Проверить снова» does, and resolves to the refusal
 * the page now shows; `null` once settled. An unknown answer keeps `last`.
 */
export async function checkWorkspaceAgain(
  last: WorkspaceRefusal,
  watch: WorkspaceWatch,
): Promise<WorkspaceRefusal | null> {
  const answer = await watch.probe();
  if (answer.kind === "unknown") return last;
  if (
    answer.kind === "routed" ||
    answer.refusal.state === "signed_out" ||
    answer.refusal.state === "identity_mismatch"
  ) {
    watch.onSettled();
    return null;
  }
  watch.onRefusal(answer.refusal);
  return answer.refusal;
}

/**
 * Asks again after each `workspaceRetryDelayMs` until a workspace server
 * answers or the session ends. Returns the function that stops it.
 */
export function watchWorkspace(
  initial: WorkspaceRefusal,
  watch: WorkspaceWatch & {
    readonly setTimer: (run: () => void, delayMs: number) => () => void;
  },
): () => void {
  let stopped = false;
  let cancelTimer: () => void = () => undefined;

  const schedule = (refusal: WorkspaceRefusal | null) => {
    const delayMs = refusal === null ? null : workspaceRetryDelayMs(refusal);
    if (stopped || refusal === null || delayMs === null) return;
    cancelTimer = watch.setTimer(() => {
      void checkWorkspaceAgain(refusal, {
        ...watch,
        onRefusal: (next) => {
          if (!stopped) watch.onRefusal(next);
        },
        onSettled: () => {
          if (!stopped) watch.onSettled();
        },
      }).then(schedule);
    }, delayMs);
  };

  schedule(initial);
  return () => {
    stopped = true;
    cancelTimer();
  };
}
